import {
  DocumentReference,
  ISODateTimeWithUTCType,
} from '@principle-theorem/shared';
import type { IPractice } from '../../../practice/practice';
import type { IKnowledgeBaseBusinessHours } from '../knowledge-base/knowledge-base-push-body';
import type {
  OvieHealthSettingsAction,
  OvieHealthSettingsSection,
} from './settings-action';
import type {
  IOvieSettingsBookingCatalogValue,
  IOvieSettingsBookingPoliciesValue,
  IOvieSettingsCallHandlingValue,
  IOvieSettingsCommunicationsValue,
  IOvieSettingsGreetingValue,
  IOvieSettingsMessagesValue,
  IOvieSettingsPractice,
  IOvieSettingsRoutingValue,
} from './settings-values';

export interface IOvieHealthSettingsRequest<
  A extends OvieHealthSettingsAction = OvieHealthSettingsAction,
> {
  action: A;
  practiceRef: DocumentReference<IPractice>;
  section?: OvieHealthSettingsSection;
  body?: Record<string, unknown>;
}

export interface IOvieHealthSettingsResponse<
  A extends OvieHealthSettingsAction = OvieHealthSettingsAction,
> {
  action: A;
  data: IOvieHealthSettingsActionPayloadMap[A];
  fetchedAt: ISODateTimeWithUTCType;
}

export interface IOvieSettingsBusinessHoursValue {
  businessHours: IKnowledgeBaseBusinessHours;
}

export interface IOvieSettingsBookingWorkflowsValue {
  cancellationPolicy: string | null;
  allowsCancellations: boolean;
  cancellationFeeWindowHours: number | null;
  allowsRescheduling: boolean;
  reschedulingPolicy: string | null;
  reschedulingWindowHours: number | null;
}

export interface IOvieHealthSettingsAggregate {
  practice: IOvieSettingsPractice;
  greeting: IOvieSettingsGreetingValue;
  messages: IOvieSettingsMessagesValue;
  businessHours: IOvieSettingsBusinessHoursValue;
  callHandling: IOvieSettingsCallHandlingValue;
  routing: IOvieSettingsRoutingValue;
  communications: IOvieSettingsCommunicationsValue;
  bookingCatalog: IOvieSettingsBookingCatalogValue;
  bookingPolicies: IOvieSettingsBookingPoliciesValue;
  bookingWorkflows: IOvieSettingsBookingWorkflowsValue;
  sectionEtags: Partial<Record<OvieHealthSettingsSection, string>>;
}

export interface IOvieHealthSettingsPatchResult {
  clinicId: string;
  section: OvieHealthSettingsSection;
  values: Record<string, unknown>;
  changed: boolean;
  sideEffects: {
    revectorization?: 'queued' | 'failed_will_retry';
  };
}

export interface IOvieHealthAgentNumber {
  agentNumber: string | null;
}

export interface IOvieHealthSettingsActionPayloadMap {
  getSettings: IOvieHealthSettingsAggregate;
  getSection: unknown;
  patchSection: IOvieHealthSettingsPatchResult;
  getAgentNumber: IOvieHealthAgentNumber;
}
